<template>
	<BudgetChartBase
		data-testid="budget-chart-revenue"
		class-name="budget-chart__chart-revenue"
		:project-id="projectId"
		:retainer-period-id="retainerPeriodId"
		:project="project"
		:loading="loading"
		:default-min-y-axis-size="1000"
		:make-y-axis-duration-options="yAxisDurationRevenue"
		:show-planned="false"
		:make-tooltip-value-formatter="makeTooltipFormatterRevenue"
		:chart-units-label="displayCurrency"
	/>
</template>

<script lang="ts" setup>
import { computed, reactive, toRefs, watch } from 'vue';
import BudgetChartBase from './BudgetChartBase.vue';
import { yAxisDurationRevenue } from '@/lib/highchartsUtils';
import { useRefetchOnSocketMessage } from '@/lib/apollo/apolloTools';
import {
	APOLLO_PARAMS_PERSISTED_QUERY,
	usePersistedQueryLoading,
} from '@/lib/apollo/apollo';
import { useSettings } from '@/composables/settings/useSettings';
import { Setting } from '@/constants/settings';
import {
	type BudgetChartQueryVariables,
	type Project,
	ProjectProgressGroupBy,
	type RetainerPeriod,
	useBudgetChartQuery,
} from '@/generated/graphql';
import { type ChartProgressGroup, type ChartProject } from '../types';
const { setting } = useSettings();
import { useFormatter } from '@/composables';
import { SOCKET_MESSAGES_THAT_TRIGGER_REFETCHING } from '@/features/Project/constants';

export interface BudgetChartRevenueProps {
	projectId: Project['id'];
	retainerPeriodId?: RetainerPeriod['id'];
}

const props = defineProps<BudgetChartRevenueProps>();

const { projectId, retainerPeriodId } = toRefs(props);

const variables = reactive<BudgetChartQueryVariables>({
	projectInput: {
		projectId: Number(projectId.value),
	},
	projectProgressInput: {
		groupBy: ProjectProgressGroupBy.Roles,
		retainerPeriodId: retainerPeriodId.value,
	},
});

const { result, loading } = useRefetchOnSocketMessage(
	usePersistedQueryLoading(
		useBudgetChartQuery(variables, APOLLO_PARAMS_PERSISTED_QUERY)
	),
	{
		messages: SOCKET_MESSAGES_THAT_TRIGGER_REFETCHING,
		filter: (message) =>
			String(message.data.project_id) === projectId.value,
	}
);

watch(retainerPeriodId, (retainerPeriodId) => {
	variables.projectProgressInput.retainerPeriodId = retainerPeriodId;
});

const displayCurrency = computed(() => {
	return setting(Setting.DISPLAY_CURRENCY);
});

const project = computed<ChartProject | undefined>(() => {
	if (result?.value?.Project) {
		return {
			...result.value.Project,
			progressData: (result.value.Project.progressData || []).map(
				(elem): ChartProgressGroup => ({
					budgetValue: elem.monetaryBudget,
					groupName: elem.groupName,
					plannedValue: 0,
					actualValue: elem.actualRevenue,
					forecastedValue:
						elem.actualRevenue + elem.forecastedRevenue,
					actualCost: elem.actualCost,
					actualRevenue: elem.actualRevenue,
					bookingsCost: elem.bookingsCost,
					costBudget: elem.costBudget,
					forecastedCost: elem.forecastedCost + elem.actualCost,
					forecastedRevenue: elem.forecastedRevenue,
					monetaryBudget: elem.monetaryBudget,
					tasksCost: elem.tasksCost,
				})
			),
		};
	}

	return undefined;
});

const { formatMoney } = useFormatter();

const makeTooltipFormatterRevenue = () => (amount: number) => {
	return formatMoney(amount);
};
</script>
